import { call, put, select, takeLatest } from 'typed-redux-saga'
import { errorHandler, notifyMessageCreator, gridSlice } from 'Modules'
import { RequestParams, ResponseCatalog } from 'Models'
import {
	partnerRequestSelectors,
	partnerRequestApi,
	PartnerRequestModel,
} from 'Src/pages/partnerRequests'
import { routerActions } from 'connected-react-router'
import { FrontUrls } from 'Constants/FrontUrls'
import { orderSelectors, ordersSlice } from 'Pages/orders_design'

import { takeEveryUnique } from 'Helpers'
import { PartnerRequestNS, partnerRequestSlice } from './slice'

function* getList(action: PartnerRequestNS.GetPartnerRequestList) {
	try {
		yield* put(gridSlice.actions.setLoading(true))
		const { params, clear, callback } = action.payload
		const { page, per_page, query, ...fields } = params
		const requestParams = new RequestParams(clear ? 1 : page, per_page, query, fields)
		if (clear) {
			yield* put(partnerRequestSlice.actions.clearAll())
		}
		const res = yield* call(partnerRequestApi.getAll, requestParams)
		const partnerRequests = new ResponseCatalog<PartnerRequestModel>(res)
		if (!clear) {
			const oldPartnerRequests = yield* select(partnerRequestSelectors.selectEntries)
			partnerRequests.entries = oldPartnerRequests.concat(res.entries)
		}
		yield* put(partnerRequestSlice.actions.setPartnerRequestList(partnerRequests))
		yield* put(partnerRequestSlice.actions.setRequestParams(requestParams))
		callback?.()
	} catch (error) {
		yield* call(errorHandler, error)
	} finally {
		yield* put(gridSlice.actions.setLoading(false))
	}
}

function* getOne(action: PartnerRequestNS.GetOne) {
	try {
		yield* put(partnerRequestSlice.actions.setIsLoading(true))
		const res = yield* call(partnerRequestApi.getOne, action.payload)
		yield* put(partnerRequestSlice.actions.setOne(res))
	} catch (error) {
		yield* call(errorHandler, error)
	} finally {
		yield* put(partnerRequestSlice.actions.setIsLoading(false))
	}
}

function* createOne(action: PartnerRequestNS.CreateOne) {
	try {
		yield* put(partnerRequestSlice.actions.setIsLoading(true))
		const res = yield* call(partnerRequestApi.createOne, action.payload)
		yield* put(partnerRequestSlice.actions.setOne(res))
		yield* put(routerActions.push(FrontUrls.partner_request.index))
	} catch (error) {
		yield* call(errorHandler, error)
	} finally {
		yield* put(partnerRequestSlice.actions.setIsLoading(false))
	}
}

function* updateOne(action: PartnerRequestNS.UpdateOne) {
	try {
		yield* put(partnerRequestSlice.actions.setIsLoading(true))
		const res = yield* call(partnerRequestApi.updateOne, action.payload.id, action.payload.data)
		yield* put(partnerRequestSlice.actions.setOne(res))
		action.payload.callback?.()
	} catch (error) {
		yield* call(errorHandler, error)
	} finally {
		yield* put(partnerRequestSlice.actions.setIsLoading(false))
	}
}

function* uploadFile(action: PartnerRequestNS.UploadFile) {
	try {
		yield* put(partnerRequestSlice.actions.setIsLoading(true))
		yield* call(partnerRequestApi.uploadFile, action.payload.id, action.payload.data)
		yield* call(notifyMessageCreator, 'common.sending.success')
		const currentRequestParams = yield* select(orderSelectors.getParams)
		yield* put(ordersSlice.actions.getAll({ params: currentRequestParams, clear: true }))
		yield* put(ordersSlice.actions.getTotalPartnerRequests())
	} catch (error) {
		yield* call(errorHandler, error)
	} finally {
		yield* put(partnerRequestSlice.actions.setIsLoading(false))
	}
}

function* setStatus(action: PartnerRequestNS.SetStatus) {
	try {
		yield* put(partnerRequestSlice.actions.setIsLoading(true))
		yield* call(partnerRequestApi.setStatus, action.payload.id, action.payload.status)
		yield* put(partnerRequestSlice.actions.getOne(action.payload.id))
	} catch (error) {
		yield* call(errorHandler, error)
	} finally {
		yield* put(partnerRequestSlice.actions.setIsLoading(false))
	}
}

function* agentComment(action: PartnerRequestNS.AgentComment) {
	try {
		yield* put(partnerRequestSlice.actions.setIsLoading(true))
		yield* call(partnerRequestApi.agentComment, action.payload.id, action.payload.data)
		yield* put(partnerRequestSlice.actions.getOne(action.payload.id))
		action.payload?.callback?.()
	} catch (error) {
		yield* call(errorHandler, error)
	} finally {
		yield* put(partnerRequestSlice.actions.setIsLoading(false))
	}
}

function* tmppComment(action: PartnerRequestNS.TmppComment) {
	try {
		yield* put(partnerRequestSlice.actions.setIsLoading(true))
		yield* call(partnerRequestApi.tmppComment, action.payload.id, action.payload.data)
		yield* put(partnerRequestSlice.actions.getOne(action.payload.id))
	} catch (error) {
		yield* call(errorHandler, error)
	} finally {
		yield* put(partnerRequestSlice.actions.setIsLoading(false))
	}
}

function* resume(action: PartnerRequestNS.Resume) {
	try {
		yield* put(partnerRequestSlice.actions.setIsLoading(true))
		yield* call(partnerRequestApi.resume, action.payload.id, action.payload.data)
		yield* put(partnerRequestSlice.actions.getOne(action.payload.id))
	} catch (error) {
		yield* call(errorHandler, error)
	} finally {
		yield* put(partnerRequestSlice.actions.setIsLoading(false))
	}
}

function* setStatusAgreement(action: PartnerRequestNS.setStatusAgreement) {
	try {
		yield* put(partnerRequestSlice.actions.setIsLoading(true))
		yield* call(partnerRequestApi.setStatusAgreement, action.payload.id, action.payload.status)
		const partnerRequest = yield* select(partnerRequestSelectors.selectItem)
		yield* put(partnerRequestSlice.actions.getOne(partnerRequest.id))
		action.payload?.callback()
	} catch (error) {
		yield* call(errorHandler, error)
	} finally {
		yield* put(partnerRequestSlice.actions.setIsLoading(false))
	}
}

function* uploadFileAgreement(action: PartnerRequestNS.UploadFileAgreement) {
	try {
		yield* put(partnerRequestSlice.actions.setIsLoading(true))
		yield* call(partnerRequestApi.uploadFileAgreement, action.payload.id, action.payload.data)
		const partnerRequest = yield* select(partnerRequestSelectors.selectItem)
		yield* put(partnerRequestSlice.actions.getOne(partnerRequest.id))
	} catch (error) {
		yield* call(errorHandler, error)
	} finally {
		yield* put(partnerRequestSlice.actions.setIsLoading(false))
	}
}

function* getFieldsRequest() {
	try {
		yield* put(partnerRequestSlice.actions.setIsLoading(true))
		const res = yield* call(partnerRequestApi.getFieldsRequest)
		yield* put(partnerRequestSlice.actions.setFieldsRequest(res))
	} catch (error) {
		yield* call(errorHandler, error)
	} finally {
		yield* put(partnerRequestSlice.actions.setIsLoading(false))
	}
}

function* deleteFileAgreement(action: PartnerRequestNS.DeleteFileAgreement) {
	try {
		yield* put(partnerRequestSlice.actions.setIsLoading(true))
		yield* call(partnerRequestApi.deleteFileAgreement, action.payload.id, action.payload.idFile)
		const partnerRequest = yield* select(partnerRequestSelectors.selectItem)
		yield* put(partnerRequestSlice.actions.getOne(partnerRequest.id))
	} catch (error) {
		yield* call(errorHandler, error)
	} finally {
		yield* put(partnerRequestSlice.actions.setIsLoading(false))
	}
}

function* uploadFileInfotecs(action: PartnerRequestNS.UploadFileInfotecs) {
	try {
		yield* put(partnerRequestSlice.actions.setIsLoading(true))
		yield* call(
			partnerRequestApi.uploadFileInfotecs,
			action.payload.requestId,
			action.payload.infotecs_files_attributes
		)
		yield* call(notifyMessageCreator, 'common.sending.success')
		yield* put(partnerRequestSlice.actions.getOne(action.payload.requestId))
	} catch (error) {
		yield* call(errorHandler, error)
	} finally {
		yield* put(partnerRequestSlice.actions.setIsLoading(false))
	}
}

function* deleteFileInfotecs(action: PartnerRequestNS.DeleteFileInfotecs) {
	try {
		yield* put(partnerRequestSlice.actions.setIsLoading(true))
		yield* call(partnerRequestApi.deleteFileInfotecs, action.payload.id)
		yield* put(partnerRequestSlice.actions.getOne(action.payload.requestId))
	} catch (error) {
		yield* call(errorHandler, error)
	} finally {
		yield* put(partnerRequestSlice.actions.setIsLoading(false))
	}
}

function* setStatusFileInfotecs(action: PartnerRequestNS.SetStatusFileInfotecs) {
	try {
		yield* put(partnerRequestSlice.actions.setIsLoading(true))
		yield* call(partnerRequestApi.setStatusFileInfotecs, action.payload.id, action.payload.status)
		yield* put(partnerRequestSlice.actions.getOne(action.payload.requestId))
	} catch (error) {
		yield* call(errorHandler, error)
	} finally {
		yield* put(partnerRequestSlice.actions.setIsLoading(false))
	}
}

function* createOrderAgreementComment(action: PartnerRequestNS.CreateOrderAgreementsComment) {
	try {
		yield* put(partnerRequestSlice.actions.setIsLoading(true))
		yield* call(partnerRequestApi.createOrderAgreementComment, action.payload.data)
		const partnerRequest = yield* select(partnerRequestSelectors.selectItem)
		yield* put(partnerRequestSlice.actions.getOne(partnerRequest.id))
	} catch (error) {
		yield* call(errorHandler, error)
	} finally {
		yield* put(partnerRequestSlice.actions.setIsLoading(false))
	}
}

function* updateOrderAgreementComment(action: PartnerRequestNS.UpdateOrderAgreementsComment) {
	try {
		yield* put(partnerRequestSlice.actions.setIsLoading(true))
		yield* call(partnerRequestApi.updateOrderAgreementComment, action.payload.data)
		const partnerRequest = yield* select(partnerRequestSelectors.selectItem)
		yield* put(partnerRequestSlice.actions.getOne(partnerRequest.id))
	} catch (error) {
		yield* call(errorHandler, error)
	} finally {
		yield* put(partnerRequestSlice.actions.setIsLoading(false))
	}
}

export function* partnerRequestSaga() {
	yield* takeLatest(partnerRequestSlice.actions.getPartnerRequestList.type, getList)
	yield* takeLatest(partnerRequestSlice.actions.getOne.type, getOne)
	yield* takeEveryUnique(partnerRequestSlice.actions.createOne.type, createOne)
	yield* takeLatest(partnerRequestSlice.actions.updateOne.type, updateOne)
	yield* takeEveryUnique(partnerRequestSlice.actions.uploadFile.type, uploadFile)
	yield* takeLatest(partnerRequestSlice.actions.setStatus.type, setStatus)
	yield* takeLatest(partnerRequestSlice.actions.agentComment.type, agentComment)
	yield* takeLatest(partnerRequestSlice.actions.tmppComment.type, tmppComment)
	yield* takeLatest(partnerRequestSlice.actions.resume.type, resume)
	yield* takeLatest(partnerRequestSlice.actions.setStatusAgreement.type, setStatusAgreement)
	yield* takeLatest(partnerRequestSlice.actions.uploadFileAgreement.type, uploadFileAgreement)
	yield* takeLatest(partnerRequestSlice.actions.getFieldsRequest, getFieldsRequest)
	yield* takeLatest(partnerRequestSlice.actions.deleteFileAgreement.type, deleteFileAgreement)
	yield* takeLatest(partnerRequestSlice.actions.uploadFileInfotecs.type, uploadFileInfotecs)
	yield* takeLatest(partnerRequestSlice.actions.deleteFileInfotecs.type, deleteFileInfotecs)
	yield* takeLatest(partnerRequestSlice.actions.setStatusFileInfotecs.type, setStatusFileInfotecs)
	yield* takeLatest(
		partnerRequestSlice.actions.createOrderAgreementsComment.type,
		createOrderAgreementComment
	)
	yield* takeLatest(
		partnerRequestSlice.actions.updateOrderAgreementsComment.type,
		updateOrderAgreementComment
	)
}
